import { CreateProviderBulkInput, CreateProviderBulkResponse, CreateProviderInput, Provider, ProviderTypeEnum } from '@/models/gen/graphql';

import { CreateClientBulkDocument } from '@/api/queries';
import { GraphApiResponse } from '@/api/core';
import { Validation } from '@/utils';
import graphApi from '@/api';

type CreateClientBulkGraphApiResponse = GraphApiResponse<typeof CreateClientBulkDocument>;

export const createClientValidator = new Validation.Validator({
  active: (val: any): Validation.Validity => ({
    valid: Validation.isNumber(val) ? true : undefined,
    defaultValue: 1,
  }),
  billingAddress: (val: any): Validation.Validity => ({
    valid: true, // !!val && new RegExp('^[^ ][0-9a-z ]', 'i').test(val) ? true : val?.length ? false : undefined,
  }),
  billingCityCode: (val: any): Validation.Validity => ({
    valid: true, // !!val && new RegExp('^[^ ][a-z ]', 'i').test(val) ? true : val?.length ? false : undefined,
  }),
  billingStateCode: (val: any): Validation.Validity => ({
    valid: true, // !!val && new RegExp('^[A-Z]{2}').test(val) ? true : val?.length ? false : undefined,
  }),
  billingZipCode: (val: any): Validation.Validity => {
    // val = parseInt(val);
    return {
      valid: true, // !!val && !isNaN(val) && `${val}`?.length === 5 ? true : val?.length ? false : undefined,
    };
  },
  contactPerson: (val: any): Validation.Validity => ({
    valid: !!val && new RegExp('^[^ ][0-9a-z ]', 'i').test(val) ? true : val?.length ? false : undefined,
  }),
  companyId: (val: any): Validation.Validity => ({
    valid: undefined,
    defaultValue: '',
  }),
  'displayName!': (val: any): Validation.Validity => ({
    valid: !!val && new RegExp('^[^ ][0-9a-z ]', 'i').test(val),
  }),
  faxNumber: (val: any): Validation.Validity => ({
    valid: !!val && !isNaN(val) && val?.length === 10 ? true : val?.length ? false : undefined,
  }),
  iataAirlineCode: (val: any): Validation.Validity => ({
    valid: undefined, // this field should always be null when creating clients
  }),
  invoiceTermId: (val: any): Validation.Validity => ({
    valid: Validation.isValidUUID(val) ? true : val?.length ? false : undefined,
  }),
  mobilePhoneNumber: (val: any): Validation.Validity => ({
    valid: !!val && !isNaN(val) && val?.length === 10 ? true : val?.length ? false : undefined,
  }),
  'name!': (val: any): Validation.Validity => ({
    valid: !!val && new RegExp('^[^ ][0-9a-z ]', 'i').test(val),
  }),
  occPhoneNumber: (val: any): Validation.Validity => ({
    valid: !!val && !isNaN(val) && val?.length === 10 ? true : val?.length ? false : undefined,
  }),
  primaryEmail: (val: any): Validation.Validity => ({
    valid: Validation.isEmail(val) ? true : val?.length ? false : undefined,
  }),
  primaryAddress: (val: any): Validation.Validity => ({
    valid: true, // !!val && new RegExp('^[^ ][0-9a-z ]', 'i').test(val) ? true : val?.length ? false : undefined,
  }),
  primaryCityCode: (val: any): Validation.Validity => ({
    valid: true, // !!val && new RegExp('^[^ ][0-9a-z ]', 'i').test(val) ? true : val?.length ? false : undefined,
  }),
  primaryPhoneNumber: (val: any): Validation.Validity => ({
    valid: !!val && !isNaN(val) && val?.length === 10 ? true : val?.length ? false : undefined,
  }),
  primaryStateCode: (val: any): Validation.Validity => ({
    valid: true, // !!val && new RegExp('^[A-Z]{2}').test(val) ? true : val?.length ? false : undefined,
  }),
  primaryZipCode: (val: any): Validation.Validity => {
    // val = parseInt(val);
    return {
      valid: true, // !!val && !isNaN(val) && `${val}`?.length === 5 ? true : val?.length ? false : undefined,
    };
  },
  referenceNumber: (val: any): Validation.Validity => ({
    valid: !!val && new RegExp('^[^ ][0-9a-z ]', 'i').test(val) ? true : val?.length ? false : undefined,
  }),
  type: (val: any): Validation.Validity => ({
    valid: undefined, // we want to always set the value to the default enum
    defaultValue: ProviderTypeEnum.Client,
  }),
  website: (val: any): Validation.Validity => ({
    valid: !!val && Validation.isValidURL(val) ? true : val?.length ? false : undefined,
  }),
});

const createClientBulkResponseSelector = (res: CreateClientBulkGraphApiResponse): CreateProviderBulkResponse =>
  !res.errors ? (res?.createClientBulk as CreateProviderBulkResponse) : undefined;

const [runCreateClientBulk] = graphApi(CreateClientBulkDocument);

const createClientBulk = async (clients: Partial<Provider>[]): Promise<CreateProviderBulkResponse> => {
  const values: CreateProviderInput[] = clients.map((client: Provider): CreateProviderInput => createClientValidator.create(client));
  const input: CreateProviderBulkInput = { values };

  const res = await runCreateClientBulk({ input });
  return createClientBulkResponseSelector(res);
};

export default createClientBulk;
